'use client'
import { useEffect } from 'react'
import { Trans } from '@lingui/react/macro'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'
import GoogleMapsScriptProvider from '@/shared/ui/providers/GoogleMapsScriptProvider'
import Header from './Header'
import Map from './Map'
import { useBooking } from '../model/bookingContext'

export function BookingLayout({ children, meetingLink }: { children: React.ReactNode; meetingLink: string }) {
  const { dispatch } = useBooking()
  const { status } = useSession()

  // Set meeting link slug from params
  useEffect(() => {
    if (meetingLink) {
      dispatch({ type: 'SET_MEETING_LINK_SLUG', payload: meetingLink })
    }
  }, [meetingLink, dispatch])

  // Check for saved state in localStorage when authenticated
  useEffect(() => {
    if (status === 'authenticated' && typeof window !== 'undefined') {
      try {
        // Use the same key format as in AuthenticationGate
        const stateKey = `bookingState_${meetingLink || 'default'}`
        const savedState = localStorage.getItem(stateKey)

        if (savedState) {
          const parsedState = JSON.parse(savedState)

          // Only restore if we have a valid state with the same meeting link
          if (parsedState && parsedState.meetingLinkSlug === meetingLink) {
            // Dispatch action to restore the entire state
            dispatch({ type: 'RESTORE_STATE', payload: parsedState })

            // Clean up after restoring
            localStorage.removeItem(stateKey)
          }
        }
      } catch (error) {
        console.error('Error restoring booking state in layout:', error)
      }
    }
  }, [status, meetingLink, dispatch])

  return (
    <GoogleMapsScriptProvider>
      <div className="h-auto flex flex-1 bg-blue-50">
        <div className="flex-1 max-w-xl sm:pl-[10px] z-10 pb-20">
          <div className="flex md:hidden bg-primary static">
            <Header meetingLink={meetingLink} />
          </div>
          <div className="hidden md:block">
            <h1 className="font-bold text-3xl mt-8 mb-4 text-black">
              <Trans>Request a consultation</Trans>
            </h1>
          </div>
          {children}
        </div>
        <div className="hidden sm:block flex-1 bg-gray-200">
          <Map meetingLinkName={meetingLink} />
        </div>
      </div>
    </GoogleMapsScriptProvider>
  )
}
